import React from 'react';
import styled from 'styled-components';
import { useDrag, DragSourceMonitor } from 'react-dnd';
import { dndTypes, tileSize } from '../helpers/constants';
import { idToTileMap } from '../helpers/tiles';
import { ReadOnly } from '../containers/ReadOnly';

export enum TileStatusEnum {
    ACTIVE = 'ACTIVE',
    DISABLED = 'DISABLED',
    DEFAULT = 'DEFAULT',
}

type TileProps = {
    id: number,
    className?: string,
    variant?: 'hand',
    onClick?: (e: any, id: number) => void,
    //onDoubleClick?: (e: React.MouseEvent<HTMLDivElement>, id: number) => void,
    onDoubleClick?: (e: any, id: number) => void,
    status?: TileStatusEnum,
    dragType?: dndTypes,
    onBeginDrag?: (monitor: DragSourceMonitor) => void,
};

type TileContainerProps = { variant?: 'hand', status: TileStatusEnum, dragType?: any };

const getBackgroundColor = (props: any) => {
    if (props.status === TileStatusEnum.ACTIVE) {
        return 'lightblue';
    } else if (props.status === TileStatusEnum.DISABLED) {
        return 'gray';
    }
    return props.theme.colors.lightYellow;
};

export const TileContainer =
    styled.div <
    TileContainerProps >
    `
    -webkit-tap-highlight-color: transparent;
    border: 1px solid black;
    width: ${(props) => (props.variant ? '35px' : `${tileSize}px`)};
    height: ${(props) => (props.variant ? '35px' : `${tileSize}px`)};
    background-color: ${(props) => getBackgroundColor(props)};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${(props) => (props.variant ? '20px' : '36px')};
    cursor: pointer;
    border-radius: 5px;
    color: black;
`;

export const TileWithDragContainer = styled.div`
    flex: 1;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TileWithDrag: React.FC<Pick<TileProps, 'id' | 'onBeginDrag'> & { dragType: dndTypes }> = ({
    children,
    id,
    dragType,
    onBeginDrag,
}) => {
    const [, dragRef] = useDrag({
        begin: (monitor) => {
            if (onBeginDrag) {
                return onBeginDrag(monitor);
            }
        },
        item: {
            type: dragType,
            id,
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    });
    return <TileWithDragContainer ref={dragRef}>{children}</TileWithDragContainer>;
};

export const Tile = React.memo(
    ({
        id,
        variant,
        className,
        onClick,
        onDoubleClick,
        status = TileStatusEnum.DEFAULT,
        onBeginDrag,
        dragType,
    }: TileProps) => {
        const letter = idToTileMap[id];

        const children = letter;
        const [touchEndTime, setTouchEndTime] = React.useState();
        const [touchStartTouches, setTouchStartTouches] = React.useState<React.TouchList>();
        const [touchStartTime, setTouchStartTime] = React.useState();
        const { readOnly } = ReadOnly.useContainer();

        if (readOnly) {
            return <TileContainer status={TileStatusEnum.DEFAULT}>{children}</TileContainer>;
        }

        return (
            <TileContainer
                status={status}
                onDoubleClick={(e) => {
                    if (onDoubleClick) {
                        onDoubleClick(e, id);
                    }
                }}
                onClick={(e) => {
                    if (onClick) {
                        onClick(e, id);
                    }
                }}
                onTouchStart={(e) => {
                    setTouchStartTouches(e.changedTouches);
                    setTouchStartTime(e.timeStamp);
                }}
                onTouchEnd={(e) => {
                    // Needed to speed up clicking
                    // If touch start was not the same as touch end, then ignore
                    e.preventDefault();
                    const { clientX: endX, clientY: endY } = e.changedTouches[0];
                    const newTime = e.timeStamp;

                    if (touchStartTouches) {
                        const { clientX: startX, clientY: startY } = touchStartTouches[0];

                        const distance = Math.sqrt(
                            Math.pow(startX - endX, 2) + Math.pow(startY - endY, 2),
                        );

                        const clickTime = newTime - touchStartTime;

                        // Manual double click
                        if (touchEndTime && newTime - touchEndTime < 200) {
                            if (onDoubleClick) {
                                onDoubleClick(e, id);
                            }
                        } else if (distance <= 10 && clickTime < 400) {
                            if (onClick) {
                                onClick(e, id);
                            }
                        }
                        setTouchEndTime(newTime);
                    }
                }}
                className={className}
                variant={variant}
                dragType={dragType}
            >
                {dragType ? (
                    <TileWithDrag onBeginDrag={onBeginDrag} dragType={dragType} id={id}>
                        {children}
                    </TileWithDrag>
                ) : (
                    children
                )}
            </TileContainer>
        );
    },
);
